import {
  TS_REGEX,
  UNICODE_SPACES_REGEX,
  ESCAPED_UNICODE_REGEX,
  MULTIPLE_SPACES_REGEX,
  MUSICAL_SIGNS_REGEX,
  INSTRUMENTAL_TAG_REGEX,
  TITLE_TAG_REGEX
} from './constants';
import type { SpacingPreference } from './types';

/**
 * Converts an LRC timestamp string (e.g., "[01:23.45]") to total milliseconds.
 *
 * @param ts Timestamp tag string including brackets.
 * @returns Milliseconds as an integer.
 */
export function parseMs(ts: string): number {
  const match = ts.match(/\[(\d+):(\d{2})(?:\.(\d+))?\]/);
  if (!match) return 0;

  const minStr = match[1];
  const secStr = match[2];
  const fracStr = match[3];

  const min = minStr ? parseInt(minStr, 10) : 0;
  const sec = secStr ? parseInt(secStr, 10) : 0;
  const frac = fracStr || '0';
  const ms = parseInt(frac.slice(0, 3).padEnd(3, '0'), 10);

  return (min * 60 + sec) * 1000 + ms;
}

/**
 * Normalizes lyric text by stripping invisible zero-width or non-breaking web spaces
 * and unescaping literal unicode representations.
 *
 * @param text Raw lyric line after timestamp stripping.
 * @param normalize Whether unicode normalization is enabled.
 */
export function cleanLyricText(text: string, normalize = true): string {
  if (!normalize) {
    return text.trim();
  }

  return text
    .replace(ESCAPED_UNICODE_REGEX, ' ')
    .replace(UNICODE_SPACES_REGEX, ' ')
    .replace(MULTIPLE_SPACES_REGEX, ' ')
    .trim();
}

/**
 * Determines whether a lyric snippet represents an instrumental pause or musical interlude.
 *
 * @param lyric Text of the lyric line without timestamps.
 * @param activeSymbol Optional currently active custom/preset musical symbol.
 */
export function isInstrumentalLyric(lyric: string, activeSymbol = ''): boolean {
  if (!lyric) return true;
  const trimmed = lyric.trim();
  if (!trimmed) return true;

  if (activeSymbol && trimmed === activeSymbol.trim()) {
    return true;
  }

  // Musical signs, emojis, decorative lines (e.g. ♪, ♫, 🎵, ✨, ---)
  if (MUSICAL_SIGNS_REGEX.test(trimmed)) {
    return true;
  }

  // Standard metadata tags like [Instrumental], (Music), [Guitar Solo], (Interlude), etc.
  if (INSTRUMENTAL_TAG_REGEX.test(trimmed)) {
    return true;
  }

  return false;
}

/**
 * Inspects LRC lines to detect whether a trailing space after timestamps (`[00:00.00] `)
 * is predominantly used.
 *
 * @param lines Array of LRC string lines.
 * @param setting User spacing preference ('auto', 'space', 'none').
 */
export function detectSpacingPreference(
  lines: readonly string[],
  setting: SpacingPreference = 'auto'
): string {
  if (setting === 'space') return ' ';
  if (setting === 'none') return '';

  let lyricLines = 0;
  let spaceLines = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || !trimmed.match(TS_REGEX)) continue;

    const afterTs = trimmed.replace(TS_REGEX, '');
    if (afterTs.trim().length > 0) {
      lyricLines++;
      if (afterTs.startsWith(' ')) {
        spaceLines++;
      }
    }
  }

  return spaceLines > lyricLines / 2 ? ' ' : '';
}

/**
 * Extracts and sanitizes the song title from [ti:...] metadata tag for safe filename creation.
 *
 * @param lrcContent Full LRC file content.
 */
export function extractSongTitle(lrcContent: string): string | null {
  const match = lrcContent.match(TITLE_TAG_REGEX);
  if (!match || !match[1]) return null;

  return match[1].replace(/[\/\\?%*:|"<>]/g, '_').trim();
}
